import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useVisitor } from '#app/hooks/useVisitor.js';
import { logger } from '#app/services/logger.js';
import { sessionStorage } from '#app/services/storage.js';
import { onIdle } from '#app/utilities/onIdle.js';
import { onUserActive } from '#app/utilities/onUserActive.js';
import { PostHogContext } from './PostHogContext.js';
import { type FeatureFlags } from '@contra/feature-flags';
import { isbot } from 'isbot';
import { type PostHog, type PostHogConfig } from 'posthog-js';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const log = logger.child({
  namespace: 'PostHogContext',
});

type PostHogProviderProps = {
  readonly children: null | ReactNode | ReactNode[];
};

/**
 * In the context of this function, "current ID" refers to foreign PostHog sync ID obtained from API, i.e.
 * It indicates that user is an authenticated user. Meanwhile, "session ID" refers to whatever ID we persist
 * in the application space for the purpose of tracking anonymous users and users switching accounts.
 *
 * Note that even though we are using Segment, we still need to initialize PostHog identity to associate
 * events with user session recordings.
 */
export const PostHogProvider: FC<PostHogProviderProps> = ({
  children,
}: PostHogProviderProps) => {
  const { playwrightTest, posthogApiToken } = useAppConfiguration();
  const {
    featureFlags: initialFeatureFlags,
    impersonator,
    sessionId,
  } = useVisitor();
  const [initialized, setInitialized] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [featureFlags, setPostHogFeatureFlags] =
    useState<Readonly<Partial<FeatureFlags>>>(initialFeatureFlags);
  const currentUser = useCurrentUser();
  const warnings = useRef<string[]>([]);
  const [posthogInstance, setPosthogInstance] = useState<null | PostHog>(null);

  const analyticsUserId = currentUser?.analyticsUserId ?? null;

  useEffect(() => {
    if (initialized) {
      return;
    }

    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      // posthog should not be initialized/used on the server
      log.warn('detected server side rendering; disabling analytics');

      return;
    }

    if (sessionStorage.getItem('CONTRA_SESSION_POSTHOG_ENABLED') === 'false') {
      log.warn('detected PostHog disabled; disabling analytics');

      return;
    }

    if (!posthogApiToken) {
      log.warn('not initializing PostHog because API token is not set');

      return;
    }

    if (impersonator) {
      log.warn('not initializing PostHog for an impersonator session');

      return;
    }

    if (playwrightTest) {
      log.warn('not initializing PostHog for a Playwright test');

      return;
    }

    log.debug('initializing PostHog');

    setInitialized(true);

    onUserActive(() => {
      if (isbot(navigator.userAgent)) {
        log.warn('detected bot; skipping posthog initialization');

        return;
      }

      void import('posthog-js').then(({ posthog }) => {
        const bootstrap = {
          // This ensures we don't generate a new anonymous ID each time we reload the page.
          distinctID: analyticsUserId
            ? analyticsUserId
            : sessionId
              ? sessionId
              : undefined,
          // bootstrap the feature flags we fetched from posthog server-side so the client-side has the correct values available immediately
          featureFlags,
          isIdentifiedID: Boolean(analyticsUserId),
        } satisfies PostHogConfig['bootstrap'];

        posthog.init(posthogApiToken, {
          advanced_disable_feature_flags: true,
          api_host: '/posthog',
          autocapture: true,
          bootstrap,
          capture_pageview: false,
          // This is needed to enable proxying recording to PostHog.
          disable_compression: true,
          disable_session_recording: true,
          loaded: () => {
            log.debug('PostHog loaded');
            log.debug(bootstrap, 'Bootstrapped Posthog user info');

            setLoaded(true);

            const posthogSessionProperties =
              posthog._calculate_event_properties('', {});

            // This variable is exposed for the purpose of being easily accessed by the ErrorBoundary.
            window.posthogSessionId = posthogSessionProperties['$session_id'];
            window.posthogWindowId =
              posthogSessionProperties['$window_id'] ?? '';
            window.posthogUserId = posthogSessionProperties['$user_id'] ?? '';

            setPosthogInstance(posthog);

            // event listener for when the Posthog library fetches new flag values, and ensures we update the local state to use the new values.
            posthog.onFeatureFlags((_keys, fetchedFeatureFlags) => {
              log.debug(
                {
                  currentFeatureFlags: featureFlags,
                  fetchedFeatureFlags,
                },
                'Posthog onFeatureFlags event called',
              );

              if (
                fetchedFeatureFlags &&
                typeof fetchedFeatureFlags === 'object' &&
                Object.keys(fetchedFeatureFlags).length > 0
              ) {
                const newFeatureFlags = {
                  ...featureFlags,
                  ...fetchedFeatureFlags,
                };

                setPostHogFeatureFlags(newFeatureFlags);

                log.debug(
                  {
                    newFeatureFlags,
                  },
                  'Updated local state with new feature flags',
                );
              }
            });

            onIdle(() => {
              // Delaying recording reduces script execution time in the critical rendering path.
              posthog.startSessionRecording();
            });
          },
          mask_all_element_attributes: false,
          mask_all_text: false,
          persistence: 'localStorage',
          session_recording: {
            maskAllInputs: false,
          },
        });
      });
    });
  }, [
    analyticsUserId,
    featureFlags,
    impersonator,
    initialized,
    playwrightTest,
    posthogApiToken,
    sessionId,
  ]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    void import('posthog-js').then(({ posthog }) => {
      if (analyticsUserId) {
        log.info({ analyticsUserId }, 'Posthog.identify non-anonymous user');

        posthog.identify(analyticsUserId);
      }
    });
  }, [analyticsUserId, loaded, sessionId]);

  const setFeatureFlags = useCallback(
    (flags: Readonly<Partial<FeatureFlags>>) => {
      setPostHogFeatureFlags(flags);
    },
    [setPostHogFeatureFlags],
  );

  const context = useMemo(() => {
    return {
      featureFlags,
      featureFlagsLoaded: true,
      posthogInstance,
      setFeatureFlags,
      warnings,
    };
  }, [featureFlags, posthogInstance, setFeatureFlags]);

  return (
    <PostHogContext.Provider value={context}>
      {children}
    </PostHogContext.Provider>
  );
};
